import { useEffect, useState } from 'react'
import type { NeighborhoodAnalysis } from '@popety-io/types'
import type {
  UseNeighborhoodAnalysisProps,
  UseNeighborhoodAnalysisReturn,
} from '../NeighborhoodContextTab.types'
import { DEFAULT_RADIUS } from '../NeighborhoodContextTab.constants'

export const useNeighborhoodAnalysis = ({
  building,
  initialNeighborhoodAnalysis,
  onFetchNeighborhoodAnalysis,
  onNeighborhoodAnalysisChange,
  t,
}: UseNeighborhoodAnalysisProps): UseNeighborhoodAnalysisReturn => {
  // State for neighborhood analysis data
  // Initialize with pre-fetched data if available
  const [neighborhoodAnalysis, setNeighborhoodAnalysis] =
    useState<NeighborhoodAnalysis | null>(initialNeighborhoodAnalysis || null)
  const [isLoadingAnalysis, setIsLoadingAnalysis] = useState(false)
  const [analysisError, setAnalysisError] = useState<string | null>(null)
  const [selectedRadius, setSelectedRadius] = useState<number>(DEFAULT_RADIUS)
  // Track if we've ever fetched to know when to use initial data
  const [hasUserChangedRadius, setHasUserChangedRadius] = useState(false)

  // Fetch neighborhood analysis
  // Skip fetch if we have initial data and user hasn't changed the radius
  useEffect(() => {
    const fetchAnalysis = async () => {
      if (!building?.building_id || !onFetchNeighborhoodAnalysis) return

      // If we have initial data, default radius is 200m, and user hasn't changed radius, skip fetch
      if (
        initialNeighborhoodAnalysis &&
        selectedRadius === DEFAULT_RADIUS &&
        !hasUserChangedRadius
      ) {
        // Use initial data, no need to fetch
        setNeighborhoodAnalysis(initialNeighborhoodAnalysis)
        return
      }

      setIsLoadingAnalysis(true)
      setAnalysisError(null)

      try {
        const analysis = await onFetchNeighborhoodAnalysis(
          building.building_id,
          selectedRadius
        )
        setNeighborhoodAnalysis(analysis)

        // Save to value_edited via callback (only when user changed radius)
        if (onNeighborhoodAnalysisChange && hasUserChangedRadius) {
          onNeighborhoodAnalysisChange(analysis)
        }
      } catch (error) {
        console.error('Failed to fetch neighborhood analysis:', error)
        setAnalysisError(
          error instanceof Error
            ? error.message
            : t('neighborhood.errors.loadFailed')
        )
      } finally {
        setIsLoadingAnalysis(false)
      }
    }

    fetchAnalysis()
  }, [
    building?.building_id,
    selectedRadius,
    onFetchNeighborhoodAnalysis,
    initialNeighborhoodAnalysis,
    hasUserChangedRadius,
  ])

  return {
    neighborhoodAnalysis,
    isLoadingAnalysis,
    analysisError,
    selectedRadius,
    setSelectedRadius,
    hasUserChangedRadius,
    setHasUserChangedRadius,
  }
}
